import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  Icon,
  type SortDirection,
  Table,
  Text,
} from '@factorialco/deprecated-design-system'
import orderBy from 'lodash/orderBy'

import { observer } from 'mobx-react'

import UserAvatar from 'modules/core/components/UserAvatar'
import createMemo from 'modules/core/hooks/createMemo'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'
import AccessGroup from 'modules/core/store/models/AccessGroup'
import type User from 'modules/core/store/models/User'

import EmployeeLabel from 'modules/reviews/components/EmployeeLabel'
import type { Reviewees } from 'modules/reviews/lib/types'
import { ReviewParticipantsContext } from 'modules/reviews/pages/performance/Show/Participants/context'
import formAccessGroupMemberships from 'modules/reviews/store/collections/formAccessGroupMemberships'
import type QuestionGroupActor from 'modules/reviews/store/models/QuestionGroupActor'
import type Review from 'modules/reviews/store/models/v2/Review'
import memberships from 'modules/teams/store/collections/memberships'
import teamsCollection from 'modules/teams/store/collections/teams'
import { employeeTeams } from 'modules/teams/store/employeeTeams'

import EditButton from './Settings'

const ReviewersLimit = 3
interface ReviewersProps {
  reviewers: Array<QuestionGroupActor | User | undefined>
}

export function ReviewersList({ reviewers }: ReviewersProps) {
  const count = reviewers.length - ReviewersLimit

  return (
    <Box gap="s8" alignItems="center" flexDirection="row">
      {reviewers.slice(0, ReviewersLimit).map((user) => (
        <Box key={user?.id} flexNoShrink>
          <UserAvatar user={user} size="s32" />
        </Box>
      ))}
      <Box flexNoShrink>{count > 0 && <Text.Normal>+ {count}</Text.Normal>}</Box>
    </Box>
  )
}
interface TeamsProps {
  user: User
}

function Teams({ user }: TeamsProps) {
  const employee = user.employee

  if (!employee) return <Text.Normal>-</Text.Normal>

  const teams = employeeTeams(teamsCollection, memberships, employee.id)

  if (!teams.length) return <Text.Normal>-</Text.Normal>

  const name = teams[0]?.attributes.name
  const count = teams.length - 1

  if (!count) return <Text.Normal>{name}</Text.Normal>

  return <Text.Normal>{`${name} + ${count}`}</Text.Normal>
}

interface ItemProps {
  form: Review
  user: User
  reviewers: QuestionGroupActor[] | User[]
  accessGroup: AccessGroup
}

function Item({ form, user, reviewers, accessGroup }: ItemProps) {
  const { navigation } = useNavigation()

  const navigate = useNavigate()

  const route = navigation.performance.reviews.id.participants.revieweesUser.fullPath({
    id: form.attributes.id,
    user_id: user.attributes.id,
  })

  const isMemberStrategy = accessGroup.attributes.strategy === AccessGroup.Strategies.members

  const membership = formAccessGroupMemberships.find({
    access_id: user.attributes.id,
    access_group_id: accessGroup.attributes.id,
  })

  return (
    <Table.Row.Router to={route}>
      <Table.Cell colSpan={2}>
        <EmployeeLabel user={user} />
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <Teams user={user} />
      </Table.Cell>
      <Table.Cell colSpan={2}>
        <ReviewersList reviewers={reviewers} />
      </Table.Cell>
      <Table.Cell>
        <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
          {membership && isMemberStrategy && (
            <EditButton membership={membership} user={user} form={form} />
          )}
          {form.isDraft ? (
            <Button onClick={() => navigate(route)} type="secondary" iconName="arrow-right" />
          ) : (
            <Box>
              <Icon.Medium icon="arrow-right" />
            </Box>
          )}
        </Box>
      </Table.Cell>
    </Table.Row.Router>
  )
}

const useFilter = createMemo((reviewees: Reviewees, search: string) =>
  Object.entries(reviewees)
    .map(([revieweeId, reviewers]) => {
      const user = users.get(Number(revieweeId))

      if (!user?.matchesFullName(search)) return null

      return {
        user,
        reviewers: reviewers.map((reviewer) => users.get(reviewer.access_id)).filter(Boolean),
      }
    })
    .filter(Boolean)
)
interface NameProps {
  form: Review
  search: string
  reviewees: Reviewees
  accessGroup: AccessGroup
  direction: SortDirection
}

const Name = observer(({ form, search, reviewees, accessGroup, direction }: NameProps) => {
  const accessGroupId = accessGroup.attributes.id
  const formId = form.id
  const filteredReviewees = useFilter(reviewees, search)
  const { loadingReviewees } = React.useContext(ReviewParticipantsContext)
  const sortedReviewees = orderBy(filteredReviewees, (reviewee) => reviewee?.user?.legalName, [
    direction,
  ])

  const { loading: loadingMemberships } = useAsyncData(
    () =>
      formAccessGroupMemberships.fetch({
        data: {
          access_group_id: accessGroupId,
          form_id: formId,
        },
        remove: false,
      }),
    [accessGroupId, formId]
  )

  const loading = loadingReviewees || loadingMemberships

  return (
    <Table.Body>
      {loading && <Table.Loading colSpan={7} />}
      {!loading && (
        <>
          {sortedReviewees.length === 0 && (
            <Table.Blank colSpan={7} text={i18n.t('surveys.index.table.body.no_participants')} />
          )}
          {sortedReviewees.map(
            (review) =>
              review && (
                <Item
                  key={review.user.attributes.id}
                  form={form}
                  user={review.user}
                  accessGroup={accessGroup}
                  reviewers={review.reviewers}
                />
              )
          )}
        </>
      )}
    </Table.Body>
  )
})
interface Props {
  form: Review
  search: string
  reviewees: Reviewees
  direction: SortDirection
}

export default function Body({ form, search, reviewees, direction }: Props) {
  const { accessGroup } = React.useContext(ReviewParticipantsContext)

  if (!accessGroup) return null

  return (
    <Name
      accessGroup={accessGroup}
      form={form}
      search={search}
      reviewees={reviewees}
      direction={direction}
    />
  )
}
